/**
 * Database rows → app objects.
 *
 * The one place a snake_case row from Supabase becomes the camelCase
 * object the rest of the app reads. These lived inside supabaseStore,
 * which cannot be imported on the server: it creates the browser client
 * at import time from import.meta.env. The deal rule set
 * (utils/dealState.js) has to read the same rows the page does, from a
 * route and from the cron sweeps, so the mappers moved here, pure, and
 * supabaseStore re-exports them unchanged.
 */

export function dbDocToApp(d) {
  const doc = {
    id: d.id,
    // assignmentId retained through Phase D for backfill-lagging rows.
    assignmentId: d.assignment_id,
    dealId: d.deal_id || null,
    interestEstimateId: d.interest_estimate_id || null,
    name: d.name,
    docType: d.doc_type, instrumentTitle: d.instrument_title,
    storagePath: d.storage_path, size: d.file_size,
    source: d.source, uploadedAt: d.uploaded_at,
    grantor: d.grantor, grantee: d.grantee,
    grantorAddress: d.grantor_address || '',
    granteeAddress: d.grantee_address || '',
    effectiveDate: d.effective_date, fileDate: d.file_date,
    volume: d.volume, page: d.page, instrumentNo: d.instrument_no,
    legalDescription: d.legal_description, originalFilename: d.original_filename,
    state: d.state || '',
    county: d.county || '',
    // Runsheet fields (migration-029).
    section: d.section || '',
    township: d.township || '',
    range: d.range || '',
    consideration: d.consideration || '',
    runsheetNotes: d.runsheet_notes || '',
  };
  doc._searchText = [
    doc.name, doc.docType, doc.grantor, doc.grantee,
    doc.instrumentNo, doc.legalDescription, doc.volume, doc.instrumentTitle,
    doc.state, doc.county,
    doc.section, doc.township, doc.range, doc.consideration, doc.runsheetNotes,
  ].filter(Boolean).join(' ').toLowerCase();
  return doc;
}

export function dbContactToApp(c) {
  return {
    id: c.id, type: c.type, firstName: c.first_name, lastName: c.last_name,
    fullName: c.full_name, companyId: c.company_id, companyName: c.company_name,
    title: c.title, name: c.name, entityType: c.entity_type,
    email: c.email, phone: c.phone, address: c.address,
    city: c.city, state: c.state, zip: c.zip, notes: c.notes,
    createdAt: c.created_at,
  };
}

export function dbDealToApp(d) {
  // Heavy JSONB columns are only included when the row carries them. The
  // deals list endpoint (DEAL_LIST_COLUMNS) omits them on purpose; if we
  // defaulted to [] / {} here they'd overwrite the values OwnerDashboard
  // hydrates via getDealFull.
  const out = {
    id: d.id, ownerName: d.owner_name, county: d.county, state: d.state,
    offerAmount: d.offer_amount, askingPrice: d.asking_price,
    stage: d.stage,
    archived: d.archived === true,
    assetForSale: d.asset_for_sale === true,
    contactId: d.contact_id,
    // The lead this deal was converted from. Null on a deal created
    // directly — the deal is not always downstream of the Lead Center.
    crmContactId: d.crm_contact_id || null,
    effectiveDate: d.effective_date, closingDate: d.closing_date,
    buyClosingDate: d.buy_closing_date, sellClosingDate: d.sell_closing_date,
    buyRateNRA: d.buy_rate_nra, sellRateNRA: d.sell_rate_nra,
    buySideAgentId: d.buy_side_agent_id, sellSideAgentId: d.sell_side_agent_id,
    coordinatorId: d.coordinator_id || null,
    coordinatorCommissionPct: d.coordinator_commission_pct == null ? null : Number(d.coordinator_commission_pct),
    // Optional. Set, and this deal carves 5% of margin for them out of
    // the Operating Fund's 20%; null and the fund keeps all 20%.
    dispositionManagerId: d.disposition_manager_id || null,
    dispositionBuyerId: d.disposition_buyer_id, lifecycle: d.lifecycle || {},
    // Which investor this deal is placed with, and through which of
    // their entities title is taken. Both or neither: the composite
    // foreign key on (investor_entity_id, investor_id) will not let an
    // entity be paired with the wrong investor.
    investorId: d.investor_id || null,
    investorEntityId: d.investor_entity_id || null,
    // What the investor actually put in. Recorded because it is the
    // record the firm's own fee tracking is done from; nothing in the
    // app computes on it.
    investorCapital: d.investor_capital == null ? null : Number(d.investor_capital),
    investorPlacedOn: d.investor_placed_on || null,
    // The discount off the quoted sell rate agreed for THIS deal, as a
    // percent. Null means nobody set one and the app default applies —
    // which is not the same as 0, and must not be flattened to it.
    // Why we bought it, for the investor package. Its own column
    // rather than `notes` — notes are a working scratchpad, and this
    // is sent to an investor.
    investorThesis: d.investor_thesis || '',
    // Per-tract rate schedule — the combined Title Chain & Interests
    // card edits this; the Pricing card reads it for the roll-up.
    // Shape: { [assignmentId]: { buyRateNRA, sellRateNRA } }.
    tractRates: (d.tract_rates && typeof d.tract_rates === 'object') ? d.tract_rates : {},
    createdBy: d.created_by || null,
    createdAt: d.created_at, updatedAt: d.updated_at,
  };
  // Columns the deals LIST query omits (payload size) get the same
  // key-presence treatment as the heavy JSONB below. Large text
  // columns also go missing from realtime UPDATE payloads when the
  // update didn't touch them (TOASTed values are absent from the WAL
  // record) — mapping them unconditionally stamped `undefined` over
  // values the app already had, which is how a freshly saved title
  // note "disappeared" the moment any other field on the deal saved.
  if ('legal_description' in d) out.legalDescription = d.legal_description;
  if ('acreage' in d) out.acreage = d.acreage;
  if ('nma' in d) out.nma = d.nma;
  if ('price_per_nma' in d) out.pricePerNma = d.price_per_nma;
  if ('priority' in d) out.priority = d.priority;
  if ('notes' in d) out.notes = d.notes;
  if ('sell_price' in d) out.sellPrice = d.sell_price;
  if ('asset_listed_at' in d) out.assetListedAt = d.asset_listed_at;
  if ('asset_sale' in d) out.assetSale = (d.asset_sale && typeof d.asset_sale === 'object') ? d.asset_sale : null;
  if ('buyer_psa' in d) out.buyerPsa = (d.buyer_psa && typeof d.buyer_psa === 'object') ? d.buyer_psa : null;
  if ('title_assignment' in d) out.titleAssignment = (d.title_assignment && typeof d.title_assignment === 'object') ? d.title_assignment : null;
  if ('parent_deal_id' in d) out.parentDealId = d.parent_deal_id || null;
  // A follow-up booked against the deal itself. Separate from the Lead
  // Center's next_follow_up, which is about the owner and can exist
  // before there is a deal at all.
  if ('next_follow_up' in d) out.nextFollowUp = d.next_follow_up || null;
  if ('next_follow_up_note' in d) out.nextFollowUpNote = d.next_follow_up_note || '';
  if ('pass_reason' in d) out.passReason = d.pass_reason || null;
  if ('pass_note' in d) out.passNote = d.pass_note || null;
  if ('passed_at' in d) out.passedAt = d.passed_at || null;
  if ('passed_by' in d) out.passedBy = d.passed_by || null;
  if ('title_notes' in d) out.titleNotes = d.title_notes;
  if ('final_title_notes' in d) out.finalTitleNotes = d.final_title_notes;
  if ('closing_conditions' in d) out.closingConditions = d.closing_conditions;
  if ('activities' in d) out.activities = Array.isArray(d.activities) ? d.activities : [];
  if ('notifications' in d) out.notifications = d.notifications || { recipientIds: [], channels: { email: true, sms: false }, stages: [] };
  if ('attached_document_ids' in d) out.attachedDocumentIds = Array.isArray(d.attached_document_ids) ? d.attached_document_ids : [];
  if ('todos' in d) out.todos = Array.isArray(d.todos) ? d.todos : [];
  if ('interest_estimates' in d) out.interestEstimates = Array.isArray(d.interest_estimates) ? d.interest_estimates : [];
  if ('wiring_info' in d) out.wiringInfo = (d.wiring_info && typeof d.wiring_info === 'object') ? d.wiring_info : {};
  if ('closing_docs' in d) out.closingDocs = Array.isArray(d.closing_docs) ? d.closing_docs : [];
  if ('assignment_ids' in d) out.assignmentIds = Array.isArray(d.assignment_ids) ? d.assignment_ids : [];
  if ('signature_requests' in d) out.signatureRequests = Array.isArray(d.signature_requests) ? d.signature_requests : [];
  if ('tract_wi_rates' in d) out.tractWIRates = (d.tract_wi_rates && typeof d.tract_wi_rates === 'object') ? d.tract_wi_rates : {};
  return out;
}

/** A deal_state_summaries row: the stored state of one deal. */
export function dbDealStateSummaryToApp(r) {
  return {
    dealId: r.deal_id,
    summary: (r.summary && typeof r.summary === 'object') ? r.summary : null,
    fingerprint: r.fingerprint || '',
    computedAt: r.computed_at || null,
    sourceUpdatedAt: r.source_updated_at || null,
  };
}

export function dbSignatureRequestToApp(r) {
  return {
    id: r.id,
    dealId: r.deal_id,
    signatureRequestId: r.signature_request_id,
    docTitle: r.doc_title,
    fileName: r.file_name,
    sellerName: r.seller_name,
    sellerEmail: r.seller_email,
    counterSignerName: r.counter_signer_name,
    counterSignerEmail: r.counter_signer_email,
    status: r.status,
    testMode: !!r.test_mode,
    createdAt: r.created_at,
    sentAt: r.sent_at,
    viewedAt: r.viewed_at,
    signedAt: r.signed_at,
    declinedAt: r.declined_at,
    declineReason: r.declined_reason,
    canceledAt: r.canceled_at,
    voidedAt: r.voided_at,
    expiresAt: r.expires_at,
    expiringNotifiedAt: r.expiring_notified_at,
    lastEventAt: r.last_event_at,
    lastEventType: r.last_event_type,
    signedDocumentId: r.signed_document_id,
    signedDocumentStoragePath: r.signed_document_storage_path,
    // As-sent copies: the outbound PDF stored at send time, and the
    // seller-signed intermediate when a counter-signature is pending.
    // Staff open these from the offer status/history modals.
    unsignedStoragePath: r.unsigned_storage_path,
    partialStoragePath: r.partial_storage_path,
    // The real terms when they were fetched; otherwise just enough of
    // the shape for signaturePurpose() to answer, which is all the board
    // asks. Rebuilt here rather than at each reader, so nothing
    // downstream has to know which fetch it came from.
    offerTerms: (r.offer_terms && typeof r.offer_terms === 'object')
      ? r.offer_terms
      : (r.signing_purpose ? { signingPurpose: r.signing_purpose } : null),
    // False on a row from the board's fetch: the amount offered is not
    // in it. A reader that must show a figure checks this rather than
    // reading 0 out of an absent object and printing it.
    offerTermsLoaded: !!(r.offer_terms && typeof r.offer_terms === 'object'),
    ccEmails: Array.isArray(r.cc_emails) ? r.cc_emails : [],
    createdByUserId: r.created_by_user_id,
    createdByName: r.created_by_name,
    createdByEmail: r.created_by_email,
    ...(r.meta && typeof r.meta === 'object' ? r.meta : {}),
  };
}

export function dbCertifiedMailJobToApp(r) {
  if (!r) return null;
  return {
    id: r.id,
    tenantId: r.tenant_id || null,
    dealId: r.deal_id,
    closingId: r.closing_id || null,
    contactId: r.contact_id || null,
    mailType: r.mail_type || 'certified_pdf',
    status: r.status || 'queued',
    provider: r.provider || 'manual',
    providerJobId: r.provider_job_id || null,
    providerStatus: r.provider_status || null,
    trackingNumber: r.tracking_number || null,
    trackingUrl: r.tracking_url || null,
    returnTrackingNumber: r.return_tracking_number || null,
    returnTrackingUrl: r.return_tracking_url || null,
    recipientName: r.recipient_name || '',
    recipientEmail: r.recipient_email || '',
    recipientPhone: r.recipient_phone || '',
    recipientAddress1: r.recipient_address1 || '',
    recipientAddress2: r.recipient_address2 || '',
    recipientCity: r.recipient_city || '',
    recipientState: r.recipient_state || '',
    recipientZip: r.recipient_zip || '',
    returnRequired: !!r.return_required,
    returnOfficeName: r.return_office_name || '',
    returnAddress1: r.return_address1 || '',
    returnAddress2: r.return_address2 || '',
    returnCity: r.return_city || '',
    returnState: r.return_state || '',
    returnZip: r.return_zip || '',
    mailedAt: r.mailed_at || null,
    deliveredAt: r.delivered_at || null,
    returnReceivedAt: r.return_received_at || null,
    createdBy: r.created_by || null,
    createdByName: r.created_by_name || '',
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    meta: r.meta && typeof r.meta === 'object' ? r.meta : {},
    documents: Array.isArray(r.certified_mail_documents)
      ? r.certified_mail_documents.map(dbCertifiedMailDocumentToApp).filter(Boolean)
      : [],
    events: Array.isArray(r.certified_mail_events)
      ? r.certified_mail_events.map(dbCertifiedMailEventToApp).filter(Boolean)
      : [],
  };
}

export function dbBuyerToApp(b) {
  return {
    id: b.id, name: b.name, company: b.company, email: b.email, phone: b.phone,
    buyerType: b.buyer_type, basins: b.basins || [], plays: b.plays || [],
    counties: b.counties || [], priceRangeMin: b.price_range_min, priceRangeMax: b.price_range_max,
    notes: b.notes,
    // Selling agent assigned to this buyer. When the buyer is set as
    // a deal's disposition buyer, the OwnerDashboard auto-pairs
    // deal.sellSideAgentId with this id.
    assignedAgentId: b.assigned_agent_id || null,
    buyerMapAccess: !!b.buyer_map_access,
    buyerMapDealIds: b.buyer_map_deal_ids || [],
    createdAt: b.created_at, updatedAt: b.updated_at,
  };
}

export function dbInvestorToApp(i) {
  return {
    id: i.id, name: i.name, company: i.company || '', email: i.email || '',
    phone: i.phone || '', investorType: i.investor_type || '',
    status: i.status || 'active', notes: i.notes || '',
    // The Paraclete representative earning on this investor's deals —
    // the same shape as buyer_profiles.assigned_agent_id.
    assignedRepId: i.assigned_rep_id || null,
    // A fraction of margin, like every rate in coordinatorSplits.js.
    repCommissionRate: i.rep_commission_rate == null ? null : Number(i.rep_commission_rate),
    createdAt: i.created_at, updatedAt: i.updated_at,
  };
}

export function dbInvestorEntityToApp(e) {
  return {
    id: e.id, investorId: e.investor_id,
    // What the deed names.
    legalName: e.legal_name || '', entityDescriptor: e.entity_descriptor || '',
    address: e.address || '', signerName: e.signer_name || '',
    signerTitle: e.signer_title || '', stateOfFormation: e.state_of_formation || '',
    // Who owns the entity taking title. Null when not yet agreed; the
    // database refuses a set that does not total 100. The rep here is the
    // investor's assigned rep — their equity, which is separate from and
    // does not replace their sell-side commission.
    paracleteOwnershipPct: e.paraclete_ownership_pct == null ? null : Number(e.paraclete_ownership_pct),
    investorOwnershipPct: e.investor_ownership_pct == null ? null : Number(e.investor_ownership_pct),
    repOwnershipPct: e.rep_ownership_pct == null ? null : Number(e.rep_ownership_pct),
    active: e.active !== false, notes: e.notes || '',
    createdAt: e.created_at, updatedAt: e.updated_at,
  };
}

export function dbCertifiedMailDocumentToApp(r) {
  if (!r) return null;
  return {
    id: r.id,
    mailJobId: r.mail_job_id,
    dealId: r.deal_id,
    documentId: r.document_id || null,
    fileName: r.file_name,
    docType: r.doc_type || null,
    storagePath: r.storage_path || null,
    sortOrder: r.sort_order || 0,
    createdAt: r.created_at,
  };
}

export function dbCertifiedMailEventToApp(r) {
  if (!r) return null;
  return {
    id: r.id,
    mailJobId: r.mail_job_id,
    dealId: r.deal_id,
    provider: r.provider || 'manual',
    providerEventId: r.provider_event_id || null,
    eventType: r.event_type,
    status: r.status || null,
    eventAt: r.event_at,
    location: r.location || null,
    payload: r.payload && typeof r.payload === 'object' ? r.payload : {},
    createdAt: r.created_at,
  };
}

export function dbUserToApp(u) {
  return {
    id: u.id, email: u.email, name: u.name, role: u.role,
    phone: u.phone || '',
    // The photo they signed in with, mirrored onto the profile so
    // TEAMMATES can see it — auth.users is not readable from a browser,
    // which is why the deal chat drew initials for everybody but you.
    avatarUrl: u.avatar_url || '',
    // Job title ("Transaction Coordinator", "Chief Operating Officer")
    // — used in offer signature blocks and anywhere the app speaks
    // for the user.
    jobTitle: u.job_title || '',
    // Position + commission rate on margin (fraction 0-1), captured at
    // invite time — the commission engine reads these off staffUsers.
    position: u.position || '',
    commissionRate: u.commission_rate == null ? null : Number(u.commission_rate),
    // The legal entity their commissions are paid into. Fills the Payee
    // Legal Name column on every settlement statement, so it is typed once
    // here rather than retyped per deal.
    payoutCompany: u.payout_company || '',
    permissions: u.permissions || [], companyId: u.company_id,
    tenantId: u.tenant_id,
    savedSignatures: Array.isArray(u.saved_signatures) ? u.saved_signatures : [],
    // Onboarding doc state (W-9 / contractor / direct deposit) — read by
    // the Admin roster chips and the User Accounting ledger.
    onboarding: u.onboarding || {},
    createdAt: u.created_at,
  };
}

export function dbTodoToApp(r) {
  return {
    id: r.id,
    dealId: r.deal_id,
    text: r.text,
    done: !!r.done,
    createdAt: r.created_at,
    completedAt: r.completed_at,
    createdBy: r.created_by,
    createdByName: r.created_by_name,
    assigneeId: r.assignee_id,
    assigneeName: r.assignee_name,
    // Instructions (migration-280): the category and the note behind the
    // line. A plain to-do is 'general' with no note.
    category: r.category || 'general',
    note: r.note || '',
    position: Number(r.position) || 0,
  };
}
